import React, { useMemo } from "react";
import { Icon } from "@coverme/icons";
import moment from "moment";
import { Link } from "react-router-dom";
import "./FacesheetTop.scss";
import { isNotNullOrWhitespace, getFormattedAddress } from "../../services/stringService";
import { Tooltip } from "../Tooltips/Tooltip";
import { TooltipMessage, TooltipTrigger } from "@coverme/elements";

function getDateFormat(date) {
    const isMoreThan1Day = moment().diff(moment(date), "days") >= 1;
    const value = isMoreThan1Day ? moment(date).format("MM.DD.YYYY hh:mm A") : moment(date).fromNow();
    return value;
}

export const FacesheetTop = ({ interview, score, programs, isIncomplete, tag, isTagEnabled }) => {
    const formattedScore = useMemo(() => {
        return score == null || score === "*" ? "*" : score;
    }, [score]);

    const encounterId = interview.encounterId;

    const pendingProgram = useMemo(() => {
        if (programs == null) {
            return null;
        }

        if (interview.encounterStatus === "Pending Decision") {
            return programs.filter(x => x.eligibilityDeterminationStatus.name === "Pending Decision")[0];
        }

        return null;
    }, [interview.encounterStatus, programs]);

    const completeProgram = useMemo(() => {
        if (programs == null) {
            return null;
        }

        if (interview.encounterStatus === "Complete") {
            return programs.filter(x => x.eligibilityStatus.name === "Approved")[0];
        }

        return null;
    }, [interview.encounterStatus, programs]);

    const selectedProgram = useMemo(() => {
        if (completeProgram != null) {
            return completeProgram;
        }

        return pendingProgram;
    }, [pendingProgram, completeProgram]);

    const coverageTitle = useMemo(() => {
        if (interview.coverage?.coverageSummary?.personCoverages?.filter(x => x.isActive)?.length === 0) {
            return "No Coverage Found";
        }

        const primaryCoverage = interview.coverage?.coverageSummary?.personCoverages?.filter(
            x => x.isPrimaryCoverage && x.isActive
        )[0];

        if (primaryCoverage?.companyName != null) {
            return primaryCoverage.companyName;
        }

        return "No Coverage Found";
    }, [interview.coverage]);

    const shouldStatusBeLink = pendingProgram != null && pendingProgram.requestingUserCanApprove === true;

    return (
        <div className="facesheet-top">
            {interview.encounterId ? (
                <div className="facesheet-top__content">
                    <div className="facesheet-top__left">
                        <h2 className="facesheet-top__name">
                            {interview.entry.patient.firstName} {interview.entry.patient.middleInitial}{" "}
                            {interview.entry.patient.lastName} {interview.entry.patient.suffix}
                        </h2>
                        <div className="facesheet-top__info">
                            <div className="facesheet-top__info-item">
                                <span className="h5">DOB </span>
                                <span className="p-bold">
                                    {moment(interview.entry.patient.dob).format("MM.DD.YYYY")}
                                </span>
                            </div>
                            {isNotNullOrWhitespace(interview.entry.patient.ssn) && (
                                <div className="facesheet-top__info-item">
                                    <span className="h5">SSN </span>
                                    <span className="p-bold">xxx-xx-{interview.entry.patient.ssn.slice(-4)}</span>
                                </div>
                            )}
                            {interview.entry?.patient != null && isNotNullOrWhitespace(interview.entry.patient.mrn) && (
                                <div className="facesheet-top__info-item">
                                    <span className="h5">MRN </span>
                                    <span className="p-bold">{interview.entry.patient.mrn}</span>
                                </div>
                            )}
                            {isNotNullOrWhitespace(interview.entry?.patient?.primaryPhoneNumber?.phoneNumber) && (
                                <div className="facesheet-top__info-item">
                                    <span className="p-bold">
                                        <Icon icon="phone" color={"greyLight"} height={22} width={15} />
                                        {interview.entry?.patient?.primaryPhoneNumber?.phoneNumber
                                            .replace(/\D+/g, "")
                                            .replace(/(\d{3})(\d{3})(\d{4})/, "$1-$2-$3")}
                                    </span>
                                </div>
                            )}
                            {isNotNullOrWhitespace(interview.entry.patient.email) && (
                                <div className="facesheet-top__info-item">
                                    <span className="p-bold">
                                        <Icon icon="email" color={"greyLight"} height={20} width={15} />
                                        {interview.entry.patient.email}
                                    </span>
                                </div>
                            )}
                            <div className="facesheet-top__info-item">
                                <span className="p-bold">
                                    <Icon icon="home" color={"greyLight"} height={21} width={20} />
                                    {getFormattedAddress(interview.entry.patient.address)}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className="facesheet-top__right">
                        <div className="facesheet-top__score-item" style={{ marginBottom: "15px"}}>
                            {formattedScore == "*" && isIncomplete === true && (
                                <Tooltip>
                                    <TooltipTrigger>
                                        <h1>{formattedScore}</h1>
                                    </TooltipTrigger>
                                    <TooltipMessage>
                                        <div>
                                            <h3>
                                                {
                                                    "The case is in progress. Confidence score will be displayed once the case is complete."
                                                }
                                            </h3>

                                        </div>
                                        
                                    </TooltipMessage>
                                </Tooltip>
                            )}
                            {formattedScore == "*" && isIncomplete === false && <h1>{"-"}</h1>}
                            {formattedScore != "*" && (
                                <>
                                    <h5 className="facesheet-top__score-item__score-footer">Confidence Score</h5>
                                    <div>
                                        <ScoreComponent score={formattedScore} isPrimaryScore={true} />
                                    </div>
                                </>
                            )}
                        </div>
                        <div className="facesheet-top__score-item">
                            <span className="h5">Current Status</span>
                            <span className="h4">
                                {shouldStatusBeLink && (
                                    <Link to={`/interview/${encounterId}/details/approval`} style={{ color: "unset" }}>
                                        {interview.encounterStatus}
                                    </Link>
                                )}
                                {!shouldStatusBeLink && interview.encounterStatus}
                            </span>
                        </div>

                        <div className="facesheet-top__score-item">
                            <span className="h5">Coverage</span>
                            <span className="h4">{coverageTitle}</span>
                        </div>
                        {selectedProgram != null && (
                            <div className="facesheet-top__score-item">
                                <span className="h5">Program</span>
                                <span className="h4">
                                    {selectedProgram?.details?.Title || selectedProgram?.assistanceCategory}
                                </span>
                            </div>
                        )}
                        {isTagEnabled && tag !== "" && tag !== undefined && tag !== null && (
                            <div className="facesheet-top__score-item">
                                <span className="h5">TAG</span>
                                <span className="h4">{tag}</span>
                            </div>
                        )}
                        <div className="facesheet-top__score-item">
                            <span className="h5">Created</span>
                            <span className="h4">{getDateFormat(interview.created)}</span>
                        </div>
                        <div className="facesheet-top__score-item">
                            <span className="h5">Updated</span>
                            <span className="h4">{getDateFormat(interview.lastUpdated)}</span>
                        </div>
                    </div>
                </div>
            ) : (
                <div>
                    <h2>Loading</h2>
                </div>
            )}
        </div>
    );
};

function ScoreComponent({ score, title, isPrimaryScore }) {
    let className = "";
    if (score > 71) {
        className = "green";
    } else if (score >= 41) {
        className = "yellow";
    } else {
        className = "red";
    }

    return (
        <div className={`facesheet-top__score ${isPrimaryScore ? "primary-score" : ""}`}>
            <div className="score-value-container">
                <div className={`score-value ${className} `}>
                    <h2>{score}</h2>
                </div>
            </div>
            <div className="score-title">{title}</div>
        </div>
    );
}
